import { action, computed, makeObservable, observable } from 'mobx';
import { Events } from '@shared/Events';
import { DateUtils } from '@shared/utils/DateUtils';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { Notify } from '@shared/ui-native/Notify';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { IFilterItem } from '@shared/ui-react/CheckboxListDropdown';
import { DocumentPropertiesService } from '@shared/services/DocumentPropertiesService';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { FilesService } from '@shared/services/FilesService';
import { IMAGE_CLASS_NONE } from '@shared/images/imageClasses';

const emptyFilter: Readonly<IDocumentFilter> = {
    showOnMap: true,
    fromTimestamp: null,
    toTimestamp: null,
    datePresetIndex: null,
    alignmentId: null,
    folderIds: [],
    text: '',
    userIds: [],
    type: '', // panorama,
    imageClasses: [],
    properties: {},
    propertiesLogicSwitches: {},
};

interface IFetchResponse {
    images?: IImage[];
    idocuments?: IDocument[];
    links: IFileShortcut[];
}

export abstract class DocumentsImagesStoreAbstract<T extends DocumentTypes> implements DateRange.IFilterStore {
    abstract type: 'images' | 'documents';
    abstract loadedEvent: string; // todo, should be removed, its now temporary hack to handle async loading and ui
    @observable public isLoadingItems = false;
    @observable public isLoadingPropertiesFilter = false;
    @observable public hasLoadedItems = false;
    @observable public fetchPromise: Promise<any> = null;
    @observable public iconScale = 1;
    @observable public sortKey: 'name' | 'date' | 'station' = 'date';

    @observable public items: T[] = [];
    @observable public links: IFileShortcut[] = [];
    @observable public itemsAndLinksAsItems: T[] = [];

    @observable public creators: Map<number, IFilterItem> = new Map();
    @observable public filter: IDocumentFilter = {
        ...emptyFilter,
    };

    @observable filterPinned = false;
    @observable filterOpened = false;

    @observable showThumbnailsForFolderId = null;
    @observable focusThumbnailId = null;

    // all filters except 'properties' are done on frontend, so we need to filter by properties on backend and combine the results
    @observable private uuidsMatchingPropertiesFilter = null; // null means dont use filter, empty array means filter and show nothing
    private filterByPropertiesAbort: AbortController = null;
    public documentProperties = null;

    constructor() {
        makeObservable(this);
    }

    public fetchAndSet = async (): Promise<any> => {
        this.isLoadingItems = true;

        this.fetchPromise = new Promise<void>((done, fail) => {
            this.fetch()
                .done(async (resp: IFetchResponse) => {
                    const items = [...(resp.images || []), ...(resp.idocuments ?? [])];
                    const links = resp.links ?? [];

                    if (Object.values(this.filter.properties).length) {
                        try {
                            await this.fetchPropertiesFilter();
                        } catch (e) {
                            console.error(e);
                        }
                    }

                    this.items = items
                        .map(item => {
                            // item should always have folder/Id, but backend can get corrupted
                            const folderId = item.folderId || (item as IDocument).folder?.id;
                            if (!folderId) {
                                console.warn('item has no folderId', item);
                                return null;
                            }
                            return {
                                name: '',
                                description: '',
                                folderId,
                                ...item,
                            };
                        })
                        .filter(Boolean) as T[];

                    links.forEach(link => {
                        link.targetDocument = items.find(item => item.id === link.targetDocumentId);
                    });
                    this.links = links;
                    this.itemsAndLinksAsItems = [
                        ...this.items,
                        ...links.map(link => {
                            const real = this.items.find(item => item.id === link.targetDocumentId);
                            return {
                                ...link,
                                ...real,
                                linkId: link.id,
                                folderId: link.folderId,
                            };
                        }),
                    ];

                    const creators: Map<number, IFilterItem> = new Map();
                    creators.set(-1, {
                        value: -1,
                        active: false,
                        title: __('general.empty'),
                    });
                    this.items.forEach(doc => {
                        if (doc.creator) {
                            creators.set(doc.creator.id, {
                                value: doc.creator.id,
                                title: doc.creator.username,
                            });
                        }
                    });
                    this.creators = observable(creators);
                    onDone();
                    done();
                })
                .fail(() => {
                    onDone();
                    fail();
                });

            const onDone = () => {
                setTimeout(() => {
                    this.hasLoadedItems = true;
                    this.isLoadingItems = false;
                    this.fetchPromise = null;
                    Events.trigger(this.loadedEvent);
                    if (this.onItemsLoaded) this.onItemsLoaded();
                }, 100);
            };
        });

        return this.fetchPromise;
    };

    public downloadFiltered() {
        const ids: number[] = this.itemsFiltered.map(img => img.id);
        return this.downloadByIds(ids, null);
    }

    @action.bound
    public setFilter(filter: Partial<IDocumentFilter>) {
        this.filter = {
            ...this.filter,
            ...filter,
        };
    }

    @action.bound
    public clearFilter() {
        this.filter.userIds = emptyFilter.userIds;
        this.filter.text = emptyFilter.text;
        this.filter.type = emptyFilter.type;
        this.filter.imageClasses = emptyFilter.imageClasses;
        this.filter.fromTimestamp = emptyFilter.fromTimestamp;
        this.filter.toTimestamp = emptyFilter.toTimestamp;
        this.filter.datePresetIndex = emptyFilter.datePresetIndex;
        this.filter.alignmentId = null;
        this.filter.showOnMap = true;
        this.uuidsMatchingPropertiesFilter = null;
        this.filter.properties = null;
    }

    @action.bound
    public setDates(start: number, end: number, presetIndex: number) {
        const [safeStart, safeEnd] = DateUtils.clampDateRange(start, end);
        this.filter.fromTimestamp = safeStart;
        this.filter.toTimestamp = safeEnd;
        this.filter.datePresetIndex = presetIndex;
    }

    public abstract downloadByIds(ids: number[], name: string): JQueryPromise<any>;

    protected abstract fetch(): JQueryPromise<any>;

    protected abstract removeByUuids(itemUuids: string[]): JQueryPromise<any>;

    protected abstract onItemsLoaded(): void;

    public removeItem(target: DocumentTypes): JQueryPromise<any> {
        if (target.linkId) {
            if (confirm(__('js_link.delete'))) {
                this.isLoadingItems = true;
                FilesService.deleteLinks([target.linkId])
                    .done(() => {
                        this.isLoadingItems = false;
                        this.links = this.links.filter(link => link.id !== target.linkId);
                        this.itemsAndLinksAsItems = this.itemsAndLinksAsItems.filter(existing => existing.linkId !== target.linkId);
                        Notify.success();
                    })
                    .fail(() => {
                        this.isLoadingItems = false;
                        Notify.error();
                    });
            }
        } else {
            return this.removeByIds([target.id]);
        }
    }

    public removeByIds(ids: number[]): JQueryPromise<any> {
        const uuids = ids.map(id => this.items.find(item => item.id === id)?.uuid);
        return this.remove(uuids);
    }

    @action
    public remove(itemUuids: string[]): JQueryPromise<any> {
        const promise = this.removeByUuids(itemUuids);
        if (promise) {
            this.isLoadingItems = true;
            return promise
                .done(() => {
                    this.isLoadingItems = false;
                    itemUuids.forEach(uuid =>
                        this.items.splice(
                            this.items.findIndex(item => item.uuid === uuid),
                            1
                        )
                    );
                    Notify.success();
                    Events.trigger('map-hide-overlays');
                    if (!!(document.querySelector('#map-photo-popup') as HTMLDivElement)?.innerText) {
                        ReactUtils.safeUnmountComponentAtNode(document.querySelector('#map-photo-popup'));
                    }
                })
                .fail(() => {
                    Notify.error();
                    this.isLoadingItems = false;
                });
        }
    }

    @computed
    public get itemsFiltered(): T[] {
        return this.getFiltered(this.filter);
    }

    @computed
    get counts() {
        const filteredFolderCounts = {};
        this.itemsFiltered.forEach(item => {
            filteredFolderCounts[item.folderId] = (filteredFolderCounts[item.folderId] || 0) + 1;
            this.links.forEach(link => {
                if (link.targetDocumentId === item.id) {
                    filteredFolderCounts[link.folderId] = (filteredFolderCounts[link.folderId] || 0) + 1;
                }
            });
        });

        const allFolderCounts = _.countBy(this.items, f => f.folderId);
        this.links.forEach(link => {
            allFolderCounts[link.folderId] = (allFolderCounts[link.folderId] || 0) + 1;
        });

        return {
            filteredFolderCounts,
            allFolderCounts,
        };
    }

    public getFolderCounts = (): Promise<any> => {
        if (this.fetchPromise) {
            return new Promise<any>(done => {
                this.fetchPromise.then(() => done(this.counts.allFolderCounts));
            });
        }
        return Promise.resolve(this.counts.allFolderCounts);
    };

    @computed
    get numberOfAppliedFilters(): number {
        let count = 0;
        if (!this.filter.showOnMap) count += 1;
        if (this.filter.text?.length) count += 1;
        if (this.filter.type?.length) count += 1;
        if (this.filter.imageClasses?.length > 0) count += 1;
        if (this.filter.userIds?.length > 0) count += 1;
        if (this.filter.alignmentId != null) count += 1;
        if (this.filter.fromTimestamp || this.filter.toTimestamp) count += 1;
        if (Object.keys(this.filter.properties ?? {}).length) count += 1;
        return count;
    }

    @computed
    public get thumbnails() {
        if (!this.showThumbnailsForFolderId) {
            return null;
        }

        const filtered = this.getFiltered(
            {
                ...this.filter,
                showOnMap: true,
                folderIds: [this.showThumbnailsForFolderId],
            },
            true
        );

        const unfiltered = this.itemsAndLinksAsItems.filter(item => item.folderId === this.showThumbnailsForFolderId);

        return {
            filtered,
            unfiltered,
        };
    }

    mergeItems = (updatedUuids: string[], newValues: Partial<T>) => {
        delete newValues.id;
        delete newValues.uuid;
        updatedUuids.forEach(uuid => {
            const existingItemIndex = this.items.findIndex(i => i.uuid === uuid);
            if (existingItemIndex !== -1) {
                const item = this.items[existingItemIndex];
                const idIndex = (newValues as any).imageIds?.findIndex(id => id === item.id);
                const newName = idIndex !== -1 && (newValues as any).names?.length ? (newValues as any).names[idIndex] : newValues.name;
                this.items[existingItemIndex] = {
                    ...item,
                    ...newValues,
                    name: newName ?? item.name,
                    icon: newValues.icon,
                };

                if (newValues.folderId && this.items[existingItemIndex].folder) {
                    this.items[existingItemIndex].folder.id = newValues.folderId;
                }

                this.itemsAndLinksAsItems
                    .filter(itemOrLink => itemOrLink.uuid === uuid)
                    .forEach(itemOrLink => {
                        itemOrLink.name = this.items[existingItemIndex].name;
                        itemOrLink.icon = this.items[existingItemIndex].icon;
                        itemOrLink.folderId = this.items[existingItemIndex].folderId;
                        itemOrLink.folder = this.items[existingItemIndex].folder;
                        itemOrLink.geographicPoint = this.items[existingItemIndex].geographicPoint;
                    });
            }
        });
    };

    getFiltered(filter: Partial<IDocumentFilter>, includeLinkedItems = false): T[] {
        const { showOnMap, text, fromTimestamp, toTimestamp, folderIds, userIds, alignmentId, type, imageClasses } = filter;

        if (!showOnMap) return [];
        if (folderIds && folderIds.length === 0) return [];

        const textSafe = (text ?? '').trim().toLowerCase();
        const [start, end] = DateUtils.clampDateRange(fromTimestamp, toTimestamp);

        let itemsFiltered = includeLinkedItems ? this.itemsAndLinksAsItems : this.items;

        // date
        if (start || end) {
            itemsFiltered = itemsFiltered.filter(doc => {
                const imgOriginalDate = parseInt((doc as IImage).originalDate, 10);
                const date = !isNaN(imgOriginalDate) ? imgOriginalDate : parseInt(doc.timestamp, 10);

                if (start && date < start) return false;
                if (end && date > end) return false;
                return true;
            });
        }

        if (folderIds && folderIds.length > 0) {
            itemsFiltered = itemsFiltered.filter(doc => {
                // we includeLinkedItems=true when filtering for thumbnails
                // otherwise we only filter for items to show on map based on checked folders, so we check links inside checked folders also
                if (includeLinkedItems) {
                    return folderIds.includes(parseInt(doc.folderId, 10));
                } else {
                    const itemLinks = this.links.filter(link => link.targetDocumentId === doc.id);
                    return folderIds.includes(parseInt(doc.folderId, 10)) || folderIds.some(folderId => itemLinks.some(link => link.folderId === folderId));
                }
            });
        }

        if (userIds.length > 0) {
            itemsFiltered = itemsFiltered.filter(doc => {
                const id = doc.creator?.id ?? -1;
                return userIds.includes(id);
            });
        }

        if (alignmentId != null) {
            itemsFiltered = itemsFiltered.filter(doc => {
                return (doc as IImage).alignmentId == alignmentId;
            });
        }

        if (textSafe != '') {
            itemsFiltered = itemsFiltered.filter(doc => {
                return doc.name.toLowerCase().indexOf(textSafe) > -1 || (doc.description ?? '').toLowerCase().indexOf(textSafe) > -1;
            });
        }

        if (this.uuidsMatchingPropertiesFilter != null) {
            itemsFiltered = itemsFiltered.filter(doc => {
                return this.uuidsMatchingPropertiesFilter.includes(doc.uuid);
            });
        }

        if (imageClasses?.length > 0) {
            itemsFiltered = itemsFiltered.filter(doc => {
                const analytics = (doc as IImage).analytics;
                // IMAGE_CLASS_NONE is mutually exclusive with real classes (enforced in DocumentsImagesFilter);
                // it matches only images whose classes were computed but came back empty — null analytics means never computed
                if (imageClasses.includes(IMAGE_CLASS_NONE)) return analytics != null && !analytics.imageClasses?.length;
                return imageClasses.some(cls => analytics?.imageClasses?.includes(cls));
            });
        }

        if (type === 'panorama') {
            itemsFiltered = itemsFiltered.filter(doc => {
                return (doc as IImage).panorama;
            });
        } else if (type === 'no_location') {
            itemsFiltered = itemsFiltered.filter(doc => {
                return !doc.geographicPoint || (doc.geographicPoint?.lon === 0 && doc.geographicPoint?.lat === 0);
            });
        }

        const dateCompare = (b, a /* newer on top */) => {
            const aOriginalDate = parseInt((a as IImage).originalDate, 10);
            const aDate = !isNaN(aOriginalDate) ? aOriginalDate : parseInt(a.timestamp, 10);
            const bOriginalDate = parseInt((b as IImage).originalDate, 10);
            const bDate = !isNaN(bOriginalDate) ? bOriginalDate : parseInt(b.timestamp, 10);
            return aDate - bDate;
        };
        if (this.sortKey === 'name') {
            return itemsFiltered.slice().sort((a, b) => alphaNumSort(a.name, b.name));
        } else if (this.sortKey === 'station') {
            return itemsFiltered
                .slice()
                .sort((a, b) => {
                    return (a.alignmentId ?? 0) - (b.alignmentId ?? 0);
                })
                .sort((a, b) => {
                    if (a.alignmentId === b.alignmentId) {
                        return (a.station ?? 0) - (b.station ?? 0);
                    }
                    return dateCompare(a, b);
                });
        } else {
            return itemsFiltered.slice().sort(dateCompare);
        }
    }

    getFilteredIgnoreMapHidden() {
        return this.getFiltered({
            ...this.filter,
            showOnMap: true,
        });
    }

    getById(id: number): T {
        if (!id) return;
        return this.items.find(item => item.id === parseInt(id + ''));
    }

    getByIds(ids: number[]): T[] {
        if (!ids) return;
        return this.items.filter(item => ids.includes(item.id));
    }

    getByUuid(uuid: string): T {
        if (!uuid) return null;
        return this.items.find(item => item.uuid === uuid);
    }

    getByUuids(uuids: string[]): T[] {
        if (!uuids) return null;
        return this.items.filter(item => uuids.includes(item.uuid));
    }

    getByFolderId(folderId: number) {
        if (!folderId) return;
        return this.items.filter(item => item.folderId === folderId);
    }

    setPropertiesFilter = (properties: Record<string, any>, logicSwitches: Record<string, 'and' | 'or'>) => {
        if (logicSwitches && Object.keys(logicSwitches).length) {
            this.filter.propertiesLogicSwitches = logicSwitches;
        }

        if (Object.keys(properties).length) {
            this.filter.properties = properties;
            if (this.hasLoadedItems) {
                this.fetchPropertiesFilterDebounced();
            }
        } else {
            this.filter.properties = null;
            this.uuidsMatchingPropertiesFilter = null;
        }
    };

    setPropertyLogicSwitch = (key: string, value: 'and' | 'or') => {
        this.filter.propertiesLogicSwitches[key] = value;
        if (this.hasLoadedItems) {
            this.fetchPropertiesFilterDebounced();
        }
    };

    fetchPropertiesFilter = async () => {
        this.isLoadingPropertiesFilter = true;
        if (this.filterByPropertiesAbort) {
            this.filterByPropertiesAbort.abort();
        }
        this.filterByPropertiesAbort = new AbortController();

        return DocumentPropertiesService.filter(this.type, this.filter.properties, this.filter.propertiesLogicSwitches, this.filterByPropertiesAbort.signal)
            .then((resp: any) => {
                this.uuidsMatchingPropertiesFilter = resp.images ?? resp.documents ?? null;
            })
            .finally(() => {
                this.isLoadingPropertiesFilter = false;
            })
            .catch(() => {
                // probably canceled
            });
    };

    fetchPropertiesFilterDebounced = _.debounce(this.fetchPropertiesFilter, 400);

    stateful = {
        defaultState: {
            checkedFolderIds: [],
            uncheckedFolderIds: [],
            timestamp: {
                from: null,
                to: null,
                preset: null,
            },
            selectedFolderId: -1,
            userIds: [],
            nameAndDescription: '',
            type: '',
            properties: null,
            propertiesLogicSwitches: {},
            sort: 'date',
            isPanelCollapsed: false,
            alignmentId: null,
            showOnMap: true,
            iconScale: 1,
        },

        restoreDefaultState: (): void => {
            this.clearFilter();
            this.stateful.restoreState(this.stateful.defaultState);
        },

        getCurrentState: (): IMapPageStateDocumentsImagesPanel => {
            return {
                timestamp: {
                    from: this.filter.fromTimestamp,
                    to: this.filter.toTimestamp,
                    preset: this.filter.datePresetIndex,
                },
                userIds: this.filter.userIds,
                nameAndDescription: this.filter.text,
                type: this.filter.type,
                sort: this.sortKey,
                properties: this.filter.properties,
                propertiesLogicSwitches: this.filter.propertiesLogicSwitches,
                showOnMap: this.filter.showOnMap,
                alignmentId: this.filter.alignmentId,
                selectedFolderId: -1,
                iconScale: this.iconScale,
            };
        },

        restoreState: (state: IMapPageStateDocumentsImagesPanel): void => {
            if (!state) {
                return null;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'timestamp')) {
                if (state.timestamp.hasOwnProperty('preset') && state.timestamp.preset >= 0 && DateUtils.presets[state.timestamp.preset]) {
                    const preset = DateUtils.presets[state.timestamp.preset];
                    this.setDates(preset.from, preset.to, state.timestamp.preset);
                } else {
                    this.setDates(state.timestamp.from, state.timestamp.to, null);
                }
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'userIds') && Array.isArray(state.userIds)) {
                this.filter.userIds = state.userIds;
            } else {
                // legacy
                if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'userId') && state.userId > 0) {
                    this.filter.userIds = [state.userId];
                }
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'nameAndDescription')) {
                this.filter.text = state.nameAndDescription;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'type')) {
                this.filter.type = state.type as any;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'sort')) {
                this.sortKey = state.sort as any;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'showOnMap')) {
                this.filter.showOnMap = state.showOnMap;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'checkedFolderIds') && Array.isArray(state.checkedFolderIds)) {
                this.filter.folderIds = state.checkedFolderIds;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'alignmentId')) {
                this.filter.alignmentId = state.alignmentId;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'properties') && Object.entries(state.properties ?? {}).length) {
                this.setPropertiesFilter(state.properties, state.propertiesLogicSwitches);
            } else {
                this.filter.properties = {};
                this.uuidsMatchingPropertiesFilter = null;
            }

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'iconScale')) {
                if (typeof state.iconScale === 'number') {
                    this.iconScale = utils.clamp(1, state.iconScale, 2);
                }
            }
        },
    };
}
